import React, { useState, useRef, useMemo, useCallback, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { FiEdit2 } from "react-icons/fi";
import { MdDeleteForever } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { ThreeDots } from "react-loader-spinner";
import { toast } from "react-toastify";
import ActionButton from "../../../Common/ActionButton";
import SearchFilter from "../../../Common/SearchFilter";
import ColumnVisibilityControl from "../../../Common/ColumnVisibilityControl";
import DeleteConfirmModal from "../../../../Common/DeleteConfirmModal";
import { getallUOM } from "../../../../Redux/actions/Operationsactions/getuom";
import { DeleteUOMApi } from "../../../../Api/Store/Uom";

/**
 * UOM (Unit of Measure) List Table (Stock Configuration)
 * Backend not yet wired – rowData defaults to [].
 */
const UOMTable = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const uomData = useSelector((state) => state.getAllUOM);
  const rolesData = useSelector((state) => state.GetAllRole);

  const gridRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [columnVisibility, setColumnVisibility] = useState({});
  const [filteredUOMs, setFilteredUOMs] = useState([]);
  const [selectedUOM, setSelectedUOM] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    dispatch(getallUOM());
  }, [dispatch]);

  useEffect(() => {
    const allData = uomData?.data || [];
    const filtered = allData.filter((item) => {
      const matchesSearch =
        !searchTerm ||
        [
          (item?.UOMName || "").toLowerCase(),
          (item?.Abbreviation || "").toLowerCase(),
          (item?.Note || "").toLowerCase(),
        ].some((field) => field.includes(searchTerm.toLowerCase()));
      return matchesSearch;
    });
    setFilteredUOMs(filtered);
  }, [searchTerm, uomData?.data]);

  const handleConfirmDelete = async () => {
    if (!selectedUOM) {
      return;
    }
    try {
      setIsDeleting(true);
      const result = await DeleteUOMApi(selectedUOM?.Id);
      if (result.data?.IsSuccess) {
        toast.success("UOM deleted successfully");
        dispatch(getallUOM());
        setSelectedUOM(null);
      } else {
        toast.error(result.data?.Message || "Failed to delete UOM");
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setIsDeleting(false);
    }
  };

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        filter: false,
        sortable: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "UOM Name",
        field: "UomName",
        flex: 2,
      },
      // {
      //   headerName: "Abbreviation",
      //   field: "Abbreviation",
      //   flex: 1,
      // },
      // {
      //   headerName: "Note",
      //   field: "Note",
      //   flex: 3,
      // },
      {
        headerName: "Actions",
        colId: "actions",
        width: 130,
        filter: false,
        sortable: false,
        suppressMenu: true,
        headerComponent: () => (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
            }}
          >
            {rolesData?.data?.UserRoles?.includes("Store Configuration UOM_Create") ? (
              <ActionButton
                text="New+"
                newBtn="true"
                onClick={() => history.push("/store/configuration/uom/add")}
              />
            ) : (
              <span>Action</span>
            )}
          </div>
        ),
        cellRenderer: (params) => (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              gap: "12px",
            }}
          >
            {rolesData?.data?.UserRoles?.includes("Store Configuration UOM_Edit") && (
              <FiEdit2
                title="Edit"
                size="1.1em"
                style={{ cursor: "pointer", color: "#4e545a" }}
                onClick={(e) => {
                  e.stopPropagation();
                  history.push("/store/configuration/uom/add", {
                    ...params.data,
                    isEdit: true,
                  });
                }}
              />
            )}
            {rolesData?.data?.UserRoles?.includes("Store Configuration UOM_Delete") && (
              <MdDeleteForever
                title="Delete"
                size="1.1em"
                className="cur_sor"
                style={{ cursor: "pointer", color: "#c0392b" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedUOM(params.data);
                }}
              />
            )}
          </div>
        ),
      },
    ],
    [history, rolesData],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      tooltipValueGetter: (p) => p.value,
      valueFormatter: (p) =>
        p.value === null || p.value === undefined || p.value === "" ? "N/A" : p.value,
      minWidth: 100,
    }),
    [],
  );

  const onGridReady = useCallback((params) => {
    const map = {};
    params.api.getColumns()?.forEach((col) => {
      if (col.getColDef().headerName) {
        map[col.getColId()] = col.isVisible();
      }
    });
    setColumnVisibility(map);
  }, []);

  const toggleColumn = useCallback(
    (colId) => {
      if (!gridRef.current?.api) {
        return;
      }
      const isCurrentlyVisible = columnVisibility[colId];
      gridRef.current.api.setColumnsVisible([colId], !isCurrentlyVisible);
      setColumnVisibility((prev) => ({ ...prev, [colId]: !isCurrentlyVisible }));
    },
    [columnVisibility],
  );

  return (
    <>
      <div
        className="d-flex flex-wrap align-items-center justify-content-end pt-3 app_font w-100"
        style={{ gap: "12px", width: "100%" }}
      >
        <div
          className="d-flex align-items-center"
          style={{
            minHeight: "30px",
            maxWidth: "100%",
            marginRight: "auto",
            padding: "4px 12px",
            color: "var(--btn-comp-text, #0b6462)",
            fontSize: "var(--font-md)",
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
          title="Unit of Measurement (UOM)"
        >
          Unit of Measurement (UOM)
        </div>
        <SearchFilter
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search"
        />
        <ColumnVisibilityControl
          gridApi={gridRef.current?.api}
          columnVisibility={columnVisibility}
          toggleColumn={toggleColumn}
        />
        {uomData?.loading && (
          <div>
            <ThreeDots color="green" height={40} width={40} />
          </div>
        )}
      </div>

      <div className="row">
        <div className="col-12 pr-0 pl-0">
          <div className="ag-theme-alpine vouchers_table_height3 mt-3" style={{ width: "100%" }}>
            <AgGridReact
              ref={gridRef}
              rowData={filteredUOMs || []}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              quickFilterText={searchTerm}
              pagination={true}
              paginationPageSize={15}
              tooltipShowDelay={0}
              onGridReady={onGridReady}
              rowStyle={{ cursor: "pointer" }}
            />
          </div>
        </div>
      </div>

      <DeleteConfirmModal
        open={!!selectedUOM}
        onClose={() => setSelectedUOM(null)}
        onConfirm={handleConfirmDelete}
        loading={isDeleting}
        message={`Are you sure you want to delete UOM "${selectedUOM?.UomName || selectedUOM?.UOMName}"?`}
        heading="Delete UOM"
      />
    </>
  );
};

export { UOMTable };
export default UOMTable;
// Trigger rebuild
